import { apiError } from "./api/client";

/**
 * "Bắt đầu khám" on an appointment with no doctor (#18899) — one rule for every screen.
 *
 * <p>Three screens create an encounter from an appointment (Tiếp đón, Khám bệnh › Hàng chờ, Chi
 * tiết lịch hẹn). The first fix guarded one of them; QA's retest found the other two still sending
 * the request and showing the server's raw "must not be null", or nothing at all. The block and the
 * sentence live here so a fourth screen cannot forget them, and the server answers the same thing
 * (422 {@code appointment_doctor_required}) for any client that gets past the button anyway.
 */
export const DOCTOR_REQUIRED_MESSAGE =
  "Lịch hẹn chưa có bác sĩ phụ trách. Vui lòng phân bác sĩ trước khi bắt đầu khám.";

/** Short label for the disabled button and its tooltip. */
export const DOCTOR_REQUIRED_HINT = "Cần phân bác sĩ trước khi bắt đầu khám";

/**
 * #19665 — Phòng = Bắt buộc (Cài đặt → Đặt lịch) and the visit has no room yet. Same shape as the
 * doctor rule: every screen disables the button and says why, and the server answers 422
 * {@code appointment_room_required} for any client that gets past it.
 */
export const ROOM_REQUIRED_MESSAGE =
  "Phòng khám đang cấu hình Phòng = Bắt buộc. Vui lòng phân phòng cho lịch hẹn trước khi bắt đầu khám.";

export const ROOM_REQUIRED_HINT = "Cần phân phòng trước khi bắt đầu khám";

/** A room is assigned. Reads the id OR the name: the reception row and the appointment carry either. */
export function hasRoom(a: { roomId?: string | null; roomName?: string | null }): boolean {
  return Boolean((a.roomId && a.roomId.trim()) || (a.roomName && a.roomName.trim()));
}

export function hasDoctor(a: { doctorId?: string | null }): boolean {
  return Boolean(a.doctorId && a.doctorId.trim());
}

/**
 * The sentence to show when starting an encounter failed. A missing doctor always reads as the one
 * Vietnamese sentence — whether the server said so by name (422 {@code appointment_doctor_required},
 * this release) or through an older build's bean-validation 400 on {@code doctorId}.
 */
export function startEncounterErrorMessage(error: unknown): string {
  const e = apiError(error);
  if (e.code === "appointment_doctor_required") return DOCTOR_REQUIRED_MESSAGE;
  if (e.code === "appointment_room_required") return ROOM_REQUIRED_MESSAGE;
  const violations = (e.problem as { violations?: Array<{ field?: string }> } | null)?.violations;
  if (e.code === "validation_failed" && violations?.some((v) => v.field === "doctorId")) {
    return DOCTOR_REQUIRED_MESSAGE;
  }
  return e.detail;
}
